"use client";

import { useEffect, useRef, useState } from "react";

import {
  CC_CURSOR_READ_PILL_SELECTOR,
  shouldHideCursorDot,
} from "@/lib/cc-cursor-read";

const INTERACTIVE_SELECTOR =
  'a, button, [role="button"], input[type="submit"], input[type="button"], .cc-btn, summary, select, .cc-cursor-target';

function getInteractiveTarget(element: Element | null, event: MouseEvent): Element | null {
  if (
    shouldHideCursorDot(element, {
      clientX: event.clientX,
      clientY: event.clientY,
      pill: document.querySelector(CC_CURSOR_READ_PILL_SELECTOR),
    })
  ) {
    return null;
  }

  const target = element?.closest(INTERACTIVE_SELECTOR);
  if (!target || target.closest("[data-cc-cursor-ignore]")) return null;
  return target;
}

function syncDotHiddenState(dot: HTMLDivElement, event: MouseEvent) {
  const hide = shouldHideCursorDot(event.target as Element | null, {
    clientX: event.clientX,
    clientY: event.clientY,
    pill: document.querySelector(CC_CURSOR_READ_PILL_SELECTOR),
  });

  dot.classList.toggle("cc-cursor-dot--hidden", hide);

  if (hide) {
    dot.classList.remove("cc-cursor-dot--active");
  }
}

export function CcCursorDot() {
  const dotRef = useRef<HTMLDivElement>(null);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const finePointer = window.matchMedia("(hover: hover) and (pointer: fine)");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    const sync = () => {
      setEnabled(finePointer.matches && !reducedMotion.matches);
    };

    sync();
    finePointer.addEventListener("change", sync);
    reducedMotion.addEventListener("change", sync);

    return () => {
      finePointer.removeEventListener("change", sync);
      reducedMotion.removeEventListener("change", sync);
    };
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const dot = dotRef.current;
    if (!dot) return;

    let activeTarget: Element | null = null;

    const onMouseMove = (event: MouseEvent) => {
      dot.style.left = `${event.clientX}px`;
      dot.style.top = `${event.clientY}px`;
      syncDotHiddenState(dot, event);
    };

    const onMouseOver = (event: MouseEvent) => {
      syncDotHiddenState(dot, event);

      const target = getInteractiveTarget(event.target as Element | null, event);
      if (!target || target === activeTarget) return;

      activeTarget = target;
      dot.classList.add("cc-cursor-dot--active");
    };

    const onMouseOut = (event: MouseEvent) => {
      syncDotHiddenState(dot, event);

      const target = getInteractiveTarget(event.target as Element | null, event);
      const related = event.relatedTarget as Element | null;

      if (!target || (related && target.contains(related))) return;

      activeTarget = null;
      dot.classList.remove("cc-cursor-dot--active");
    };

    const hoverItems = document.querySelectorAll<HTMLElement>(".hover-this");

    const animateHoverItem = (event: Event) => {
      const item = event.currentTarget as HTMLElement;
      const hoverAnim = item.querySelector<HTMLElement>(".hover-anim");
      if (!hoverAnim || !(event instanceof MouseEvent)) return;

      const { offsetX: x, offsetY: y } = event;
      const { offsetWidth: width, offsetHeight: height } = item;
      const move = 25;
      const xMove = (x / width) * (move * 2) - move;
      const yMove = (y / height) * (move * 2) - move;

      hoverAnim.style.transform = `translate(${xMove}px, ${yMove}px)`;
      if (event.type === "mouseleave") hoverAnim.style.transform = "";
    };

    window.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseover", onMouseOver);
    document.addEventListener("mouseout", onMouseOut);

    hoverItems.forEach((item) => {
      item.addEventListener("mousemove", animateHoverItem);
      item.addEventListener("mouseleave", animateHoverItem);
    });

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseover", onMouseOver);
      document.removeEventListener("mouseout", onMouseOut);

      hoverItems.forEach((item) => {
        item.removeEventListener("mousemove", animateHoverItem);
        item.removeEventListener("mouseleave", animateHoverItem);
      });
    };
  }, [enabled]);

  if (!enabled) return null;

  return <div ref={dotRef} className="cc-cursor-dot" aria-hidden="true" />;
}
